// Page dispatcher. Every page is a full page load (links are plain <a href>),
// so there is no client-side router and no teardown between views.
import './styles.css';
import { invitePage, loginPage, verifyPage } from './ui/auth-pages.ts';
import { boardPage } from './ui/board-page.ts';
import { membersPage } from './ui/members-page.ts';
import { errorPage, loading } from './ui/page.ts';
import { createWorkspacePage, homePage, workspacePage } from './ui/workspace-pages.ts';
import { localBoardPage, newLocalBoardPage } from './ui/local-board-page.ts';
import { planPage } from './ui/plan-page.ts';
import { keepPage } from './ui/keep-page.ts';
import { trashPage } from './ui/trash-page.ts';
import { publicBoardPage } from './ui/public-board-page.ts';
import { settingsPage } from './ui/settings-page.ts';
import { accountPage, confirmEmailPage } from './ui/account-page.ts';
import { trackPageView } from './analytics.ts';
import { installErrorReporting, reportError } from './errors.ts';

type Page = (match: RegExpExecArray) => void | Promise<void>;

const PAGES: [RegExp, Page][] = [
  [/^\/$/, () => homePage()],
  [/^\/login$/, () => loginPage()],
  [/^\/auth\/verify$/, () => verifyPage()],
  [/^\/invite\/([\w-]+)$/, (m) => invitePage(m[1]!)],
  [/^\/new-workspace$/, () => createWorkspacePage()],
  [/^\/w\/([\w-]+)$/, (m) => workspacePage(m[1]!)],
  [/^\/w\/([\w-]+)\/members$/, (m) => membersPage(m[1]!)],
  [/^\/w\/([\w-]+)\/plan$/, (m) => planPage(m[1]!)],
  [/^\/w\/([\w-]+)\/trash$/, (m) => trashPage(m[1]!)],
  [/^\/w\/([\w-]+)\/settings$/, (m) => settingsPage(m[1]!)],
  [/^\/account$/, () => accountPage()],
  [/^\/account\/email$/, () => confirmEmailPage()],
  [/^\/keep\/([\w.-]+)$/, (m) => keepPage(m[1]!)],
  // The operator console loads separately, so nobody else downloads it (spec §15).
  [/^\/admin$/, async () => (await import('./ui/admin/admin-page.ts')).adminPage()],
  [/^\/b\/([\w-]+)$/, (m) => boardPage(m[1]!)],
  [/^\/p\/([\w-]{22})$/, (m) => publicBoardPage(m[1]!)],
  [/^\/local\/new$/, () => newLocalBoardPage()],
  [/^\/local\/([\w-]+)$/, (m) => localBoardPage(m[1]!)],
];

async function run(): Promise<void> {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (path !== '/admin') trackPageView();
  for (const [re, page] of PAGES) {
    const m = re.exec(path);
    if (!m) continue;
    loading();
    try {
      await page(m);
    } catch (err) {
      // A 401 already navigated to the sign-in page.
      if ((err as { status?: number }).status !== 401) errorPage(err);
      reportError(err);
    }
    return;
  }
  errorPage(Object.assign(new Error('Not found'), { status: 404 }));
}

installErrorReporting();
void run();
